/**
 * `saved_views_portfolio` — the caller's saved filters as a portfolio (DSA).
 *
 * The one block the ENGINE cannot compute. Each saved filter of the viewer
 * ("Filtros guardados") is a comparable scope they chose — a size band, a
 * panel, a list of models — and this block reads the report's own KPIs
 * under each one and lines them up: a card per view with its state (the
 * brand leads / ties / trails / is absent), and a strip on top with the
 * win rate, how many views are behind, the position distribution and who
 * leads each view. The prototype's "segments", without a segment builder.
 *
 * Rules, the same family as `AiReadoutCard`:
 *
 *   1. **Not SQL, so it fetches on its own.** The host's data source
 *      implements `fetchSavedViewsPortfolio` (the portal: per user, from
 *      the warm first-page cache). A host without it — the builder
 *      preview has no caller — gets an honest note, never an empty card.
 *   2. **Every number is the report's KPI**, read under the view's own
 *      filters; no second formula lives here. The state comes from the
 *      chart's `portfolio.status` bands over the status KPI value and the
 *      leader from the winner KPI's caption — both computed by the host,
 *      labelled and coloured by the config.
 *   3. **A failure is visible.** Error card with retry on the first load;
 *      a stale marker when a refresh fails with cards on screen.
 *   4. **Clicking a card applies the view** through the host — the same
 *      write as the saved-filters drawer — and jumps to `action.tab`
 *      (or opens the sibling report `action.report` of a report group).
 *   5. **Says what it is** (JP, 2026-10-26: the tab read as confusing).
 *      With views, a header carries the title, the config's subtitle and
 *      "Save current filters"; a card lists only the filters its view
 *      changes from the report's defaults ("Whole catalog" when none);
 *      KPI labels wrap instead of being cut; the position legend lists
 *      the states that have views.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Callout, ShareBreakdown, Spinner, toneStyle } from "@pervasivemind/bi-components";

import { BlockErrorCard } from "./BlockStatus";
import type {
  DashboardDataSource,
  SavedViewEntry,
  SavedViewRef,
  SavedViewsPortfolio,
} from "./dashboard-data";
import { formatFilterValue, resolveFilterLabel } from "./filter-format";
import { formatValue, type ValueFormat } from "./formatting";
import { useLang, type Lang } from "./i18n-binding";
import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import type { ChartConfig, DashboardFilter, SavedViewsPortfolioConfig } from "./types";

export interface SavedViewsPortfolioCardProps {
  dataSource: DashboardDataSource;
  cfg: ChartConfig;
  /** The report's filters: each card prints its view as chips. */
  filters: ReadonlyArray<DashboardFilter>;
  title?: string;
  /** Apply this saved view (host write) and open the sibling report. */
  onOpenView?: (view: SavedViewRef, target: { report: string | null }) => void;
  /** Open the host's saved-filters drawer (the empty state's call). */
  onSaveView?: () => void;
  /** Changes when the caller's saved filters changed: refetch. */
  refreshKey?: number | string;
}

const FALLBACK_COLOR = toneStyle("neutral").fill;

interface StateMeta {
  key: string;
  label: string;
  color: string;
}

/** The states in display order: the bands' states, then the "no
 *  presence" one — unique, labelled and coloured by the config. */
function orderedStates(
  portfolio: SavedViewsPortfolioConfig,
  lang: Lang,
): StateMeta[] {
  const seen = new Set<string>();
  const keys = [
    ...portfolio.status.thresholds.map((b) => b.state),
    portfolio.status.none_state,
  ].filter((k) => (seen.has(k) ? false : (seen.add(k), true)));
  return keys.map((key) => {
    const spec = portfolio.status.states[key];
    return {
      key,
      label: resolveLocalized((spec?.label ?? key) as DisplayLabel, lang) || key,
      // The config names the state's tone (required by the engine); the
      // colour is the v3 fill of that tone (look v3, DQ2).
      color: toneStyle(spec?.tone ?? "neutral").fill,
    };
  });
}

/** Alpha-tinted background from a hex colour ("#15803d" → "#15803d1f"). */
function tint(color: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? `${color}1f` : "transparent";
}

export function SavedViewsPortfolioCard({
  dataSource,
  cfg,
  filters,
  title,
  onOpenView,
  onSaveView,
  refreshKey,
}: SavedViewsPortfolioCardProps): JSX.Element {
  const { t } = useTranslation();
  const lang = useLang();
  const [data, setData] = useState<SavedViewsPortfolio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Generation guard: a slow response for an older request must never
  // overwrite a newer one (the dashboard's rule for every fetch).
  const genRef = useRef(0);
  const fetcher = dataSource.fetchSavedViewsPortfolio;
  const itemId = cfg.item_id ?? "";
  const portfolio = cfg.portfolio ?? null;

  const load = useCallback(() => {
    if (!fetcher || !itemId) return;
    const gen = ++genRef.current;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetcher({ itemId, language: lang, signal: controller.signal })
      .then((res) => {
        if (gen !== genRef.current) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (gen !== genRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (gen === genRef.current) setLoading(false);
      });
    return () => controller.abort();
  }, [fetcher, itemId, lang]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
    // `refreshKey` is the host's "your saved filters changed" signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshKey]);

  const states = useMemo(
    () => (portfolio ? orderedStates(portfolio, lang) : []),
    [portfolio, lang],
  );
  const stateByKey = useMemo(
    () => new Map(states.map((s) => [s.key, s])),
    [states],
  );
  const filterByKey = useMemo(
    () => new Map(filters.map((f) => [f.key, f])),
    [filters],
  );
  const subtitle = resolveLocalized((cfg.subtitle ?? null) as DisplayLabel, lang) || "";

  if (!fetcher) {
    return (
      <Callout tone="neutral" title={title} className="h-full">
        {t("dashboard.portfolio.unavailable")}
      </Callout>
    );
  }
  if (!portfolio) {
    // Discovery refuses a portfolio chart without its block; a host that
    // still mounts one gets the same honest note, not an empty card.
    return (
      <Callout tone="neutral" title={title} className="h-full">
        {t("dashboard.portfolio.unavailable")}
      </Callout>
    );
  }
  if (error && !data) {
    return <BlockErrorCard message={error} onRetry={load} />;
  }
  if (loading && !data) {
    return (
      <div className="flex h-full items-center gap-2 px-4 py-3 text-xs text-neutral-500">
        <Spinner size={16} ariaLabel={t("common.loading", { defaultValue: "Loading" })} />
        {t("dashboard.portfolio.loading")}
      </div>
    );
  }
  if (!data) {
    return <div className="h-full" />;
  }

  const summary = data.summary;
  const total = summary.views;
  const actionTarget = {
    report: portfolio.action?.report ?? null,
  };
  const actionLabel =
    resolveLocalized((portfolio.action?.label ?? null) as DisplayLabel, lang) ||
    t("dashboard.portfolio.card.open");
  const behindLabel =
    (portfolio.status.behind_states[0] &&
      stateByKey.get(portfolio.status.behind_states[0])?.label) ||
    t("dashboard.portfolio.summary.behind");

  const chipsOf = (view: SavedViewRef): string[] => {
    const chips: string[] = [];
    for (const [key, raw] of Object.entries(view.filters)) {
      if (raw == null || raw === "") continue;
      const filt = filterByKey.get(key);
      if (!filt) continue;
      // A value equal to the report's default is not a choice of the
      // view: "Price: Effective · Seller type: Both" on every card was
      // noise that read as a filter.
      if (filt.default_value != null && String(raw) === String(filt.default_value)) continue;
      const label = resolveFilterLabel(filt, t, lang);
      const value = formatFilterValue(filt, raw, { t, locale: lang });
      chips.push(value ? `${label}: ${value}` : label);
    }
    if (view.sku) chips.push(t("filterbar.skuListCodes", { n: view.sku.count }));
    return chips;
  };

  // Card KPIs arrive as the engine's raw numbers: a `percent` KPI is a
  // fraction (gap 1.13 = +113%), so say so instead of letting the
  // formatter guess from the magnitude — the guess reads 1.13 as 1.1%.
  const kpiText = (entry: SavedViewEntry["kpis"][number]): string =>
    entry.value == null || entry.value === ""
      ? "—"
      : typeof entry.value === "string"
        ? entry.value
        : formatValue(
            entry.value,
            (entry.format ?? "number") as ValueFormat,
            entry.format === "percent" ? { fraction: true } : {},
          );

  if (total === 0) {
    return (
      <Callout tone="neutral" title={title} className="h-full">
        <div className="space-y-2">
          <p className="font-semibold text-neutral-800">{t("dashboard.portfolio.empty.title")}</p>
          <p className="text-sm text-neutral-700">{t("dashboard.portfolio.empty.body")}</p>
          {onSaveView && (
            <button
              type="button"
              onClick={onSaveView}
              className="text-sm font-semibold text-primary-ink underline underline-offset-2 hover:text-primary-ink"
            >
              {t("dashboard.portfolio.empty.cta")}
            </button>
          )}
        </div>
      </Callout>
    );
  }

  // Only the states that have views: five labels in a quarter-width card
  // were cut to "Leadi… 0 of 1".
  const segments = states
    .map((s) => ({
      key: s.key,
      label: s.label,
      value: summary.status_counts[s.key] ?? 0,
      color: s.color,
    }))
    .filter((s) => s.value > 0);

  return (
    <div
      className="flex h-full flex-col gap-3"
      data-testid="saved-views-portfolio"
      aria-busy={loading || undefined}
    >
      <div className="flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {title && <h3 className="text-sm font-bold text-neutral-900">{title}</h3>}
          {subtitle && <p className="text-xs text-neutral-600">{subtitle}</p>}
        </div>
        {onSaveView && (
          <button
            type="button"
            onClick={onSaveView}
            className="shrink-0 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-800 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            data-testid="portfolio-save-view"
          >
            {t("dashboard.portfolio.header.save")}
          </button>
        )}
      </div>

      {/* Summary strip: the four reads of the prototype's Resumen —
          win rate, behind, position (sum-100 bar), leadership. */}
      <div className={`grid shrink-0 grid-cols-2 gap-2 xl:grid-cols-4 ${loading ? "opacity-60" : ""}`}>
        <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2">
          <div className="text-xs font-semibold text-neutral-600">
            {t("dashboard.portfolio.summary.winRate")}
          </div>
          <div className="text-2xl font-bold tabular-nums text-neutral-900">
            {summary.win_rate == null ? "—" : formatValue(summary.win_rate, "percent")}
          </div>
          <div className="text-xs text-neutral-600">
            {t("dashboard.portfolio.summary.winRateHint", { lead: summary.lead, total })}
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2">
          <div className="text-xs font-semibold text-neutral-600">{behindLabel}</div>
          <div className="text-2xl font-bold tabular-nums text-neutral-900">
            {summary.behind}
            <span className="text-sm font-semibold text-neutral-600"> / {total}</span>
          </div>
          <div className="text-xs text-neutral-600">
            {t("dashboard.portfolio.summary.behindHint", { n: summary.behind, total })}
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2">
          <div className="text-xs font-semibold text-neutral-600">
            {t("dashboard.portfolio.summary.position")}
          </div>
          <div className="text-xs text-neutral-600">
            {t("dashboard.portfolio.summary.positionHint", {
              present: summary.with_presence,
              total,
            })}
          </div>
          <div className="mt-1">
            <ShareBreakdown
              segments={segments}
              total={total}
              ofLabel={t("dashboard.portfolio.summary.of")}
              barHeight={10}
              entrance={false}
              ariaLabel={t("dashboard.portfolio.summary.position")}
            />
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2">
          <div className="text-xs font-semibold text-neutral-600">
            {t("dashboard.portfolio.summary.leaders")}
          </div>
          <div className="text-xs text-neutral-600">
            {t("dashboard.portfolio.summary.leadersHint")}
          </div>
          <ul className="mt-1 space-y-1" aria-label={t("dashboard.portfolio.summary.leaders")}>
            {summary.leaders.map((leader) => (
              <li key={leader.label} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-neutral-800">
                    {leader.label}
                    {leader.focus && (
                      <span className="ml-1 rounded bg-neutral-900 px-1 text-[11px] font-bold uppercase tracking-wide text-white">
                        {t("dashboard.portfolio.card.focus")}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums text-neutral-600">
                    {leader.views} · {formatValue(leader.share, "percent")}
                  </span>
                </div>
                <div className="mt-0.5 h-1.5 w-full rounded bg-neutral-100">
                  <div
                    className="h-1.5 rounded bg-primary-dark"
                    style={{ width: `${Math.max(2, Math.round(leader.share * 100))}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {error && (
        <p className="shrink-0 text-[11px] font-semibold text-danger-600">
          {t("dashboard.portfolio.stale")}
        </p>
      )}

      {/* One card per saved view. */}
      <div
        className={`min-h-0 flex-1 overflow-y-auto ${loading ? "opacity-60" : ""}`}
      >
        <ul
          className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]"
          aria-label={title ?? t("dashboard.portfolio.card.open")}
        >
          {data.views.map((view) => {
            const state = stateByKey.get(view.status);
            const color = state?.color ?? FALLBACK_COLOR;
            const chips = chipsOf(view.saved);
            const body = (
              <>
                <div className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-bold text-neutral-900">{view.saved.name}</span>
                  <span
                    className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold"
                    style={{ color, backgroundColor: tint(color) }}
                    data-state={view.status}
                  >
                    {state?.label ?? view.status}
                  </span>
                </div>
                <div className="mt-1 min-h-[1.25rem] text-xs text-neutral-600">
                  {chips.length ? chips.join(" · ") : t("dashboard.portfolio.card.noFilters")}
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                  {portfolio.card_kpis.map((id) => {
                    const entry = view.kpis.find((k) => k.item_id === id);
                    if (!entry) return null;
                    return (
                      <div key={id} className="min-w-0">
                        <dt className="line-clamp-2 text-[11px] leading-tight text-neutral-600">
                          {resolveLocalized(entry.label as DisplayLabel, lang)}
                        </dt>
                        <dd className="truncate text-sm font-semibold tabular-nums text-neutral-900">
                          {kpiText(entry)}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
                {view.winner && (
                  <div className="mt-2 text-xs text-neutral-700">
                    {t("dashboard.portfolio.card.leader", { brand: view.winner })}
                  </div>
                )}
                {onOpenView && (
                  <div className="mt-2 text-xs font-semibold text-primary-ink">{actionLabel}</div>
                )}
              </>
            );
            const className =
              "h-full w-full rounded-lg border border-neutral-200 bg-white p-3 text-left shadow-sm";
            return (
              <li key={view.saved.id} className="min-w-0">
                {onOpenView ? (
                  <button
                    type="button"
                    className={`${className} transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
                    style={{ borderLeft: `4px solid ${color}` }}
                    onClick={() => onOpenView(view.saved, actionTarget)}
                    data-testid="saved-view-card"
                  >
                    {body}
                  </button>
                ) : (
                  <div className={className} style={{ borderLeft: `4px solid ${color}` }} data-testid="saved-view-card">
                    {body}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
